import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/tooltip'
import type { Sport, UnitSystemKeys } from '@frontend/core'
import { WebSocketController } from '@frontend/core'
import { useLazyGetActiveSessionQuery } from '@frontend/core/lib/domains/sessions'
import type { SessionDownloadOption } from '@frontend/core/lib/domains/sessions/download-definitions'
import { SESSION_DOWNLOAD_DEFINITIONS } from '@frontend/core/lib/domains/sessions/download-definitions'
import { unitSystemOptions } from '@frontend/core/lib/domains/units'
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@frontend/ui'
import { Monitor, Radio, Wifi, WifiOff } from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { useAppSelector } from '../../store/hooks'
import { useUnitsSystem } from '../units'
import { SessionContext } from './session.context'

export const SportableLiveSessionData = () => {
  const { connections } = useAppSelector((state) => state.servers)

  const [getActiveSession] = useLazyGetActiveSessionQuery()

  // Connect to session data stream //
  useEffect(() => {
    if (connections.metricsServer.connected) {
      getActiveSession()
    }
  }, [connections.metricsServer.connected])

  return null
}

export const CloudLiveIndicator = () => {
  const { formattedSession } = useContext(SessionContext)
  const { hostname, id } = formattedSession
  const [wsStatus, setWsStatus] = useState<{
    events: string
    livePosition: string
  }>({ events: 'Unknown', livePosition: 'Unknown' })

  useEffect(() => {
    const checkWebSocketStatus = () => {
      const eventsChannel = `ws/session/${id}/events`
      const livePositionChannel = `ws/session/${id}/livePosition`

      const getStatus = (channel: string) => {
        const ws = WebSocketController.websockets[channel]
        if (!ws) return 'Not connected'

        const readyState = ws.socket.readyState
        switch (readyState) {
          case 0:
            return 'Connecting'
          case 1:
            return 'Connected'
          case 2:
            return 'Closing'
          case 3:
            return 'Closed'
          default:
            return 'Unknown'
        }
      }

      setWsStatus({
        events: getStatus(eventsChannel),
        livePosition: getStatus(livePositionChannel)
      })
    }

    checkWebSocketStatus()
    const interval = setInterval(checkWebSocketStatus, 1000)

    return () => clearInterval(interval)
  }, [id])

  return (
    <div>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className='flex items-center gap-2'>
            <Radio className='h-5 w-5 animate-pulse text-red-500' />
            <span className='text-sm font-medium'>LIVE</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side='bottom'>
          <div className='flex min-w-[180px] flex-col gap-2'>
            <div className='flex items-center gap-2 font-medium'>
              <Monitor className='h-4 w-4' />
              <span>{hostname || 'Unknown'}</span>
            </div>
            <div className='flex flex-col gap-1 border-t border-gray-600 pt-2 text-xs'>
              <div className='flex items-center justify-between gap-2'>
                <span>Events:</span>
                <div className='flex items-center gap-1'>
                  {wsStatus.events === 'Connected' ? (
                    <Wifi className='h-3 w-3 text-green-500' />
                  ) : (
                    <WifiOff className='h-3 w-3 text-red-500' />
                  )}
                  <span className='opacity-80'>{wsStatus.events}</span>
                </div>
              </div>
              <div className='flex items-center justify-between gap-2'>
                <span>Live Position:</span>
                <div className='flex items-center gap-1'>
                  {wsStatus.livePosition === 'Connected' ? (
                    <Wifi className='h-3 w-3 text-green-500' />
                  ) : (
                    <WifiOff className='h-3 w-3 text-red-500' />
                  )}
                  <span className='opacity-80'>{wsStatus.livePosition}</span>
                </div>
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

export interface DownloadFormState {
  handleProceed: (types: SessionDownloadOption[], unit: string) => void
  handleClose: () => void
  sport: Sport
  options: SessionDownloadOption[]
  labels: Partial<Record<SessionDownloadOption, string>>
  // Defaults to Button's own default (filled) look — MT's call site doesn't
  // pass this and stays unaffected. Reporting passes 'outline' for its own
  // contrast needs without changing MT's Cancel button.
  cancelVariant?: 'default' | 'outline'
}

interface DownloadOptionsListProps {
  options: SessionDownloadOption[]
  labels: Partial<Record<SessionDownloadOption, string>>
  selected: SessionDownloadOption[]
  onToggle: (checked: boolean, type: SessionDownloadOption) => void
  unit: string
  onUnitChange: (unit: UnitSystemKeys) => void
}

// Presentational only — owns no state, entirely controlled by props.
// `options` determines both group membership (via supportsUnit) and each
// group's display order — callers that care about a specific order (e.g.
// packets sorting after pitch anchor / session players) pre-sort `options`
// themselves rather than this component imposing one.
export const DownloadOptionsList = ({
  options,
  labels,
  selected,
  onToggle,
  unit,
  onUnitChange
}: DownloadOptionsListProps) => {
  const unitScopedOptions = options.filter(
    (option) => SESSION_DOWNLOAD_DEFINITIONS[option].supportsUnit
  )
  const fixedUnitOptions = options.filter(
    (option) => !SESSION_DOWNLOAD_DEFINITIONS[option].supportsUnit
  )

  const isTypeSelected = (type: SessionDownloadOption) =>
    selected.includes(type)

  const renderCheckbox = (option: SessionDownloadOption) => (
    <div key={option} className='flex items-center space-x-2'>
      <Checkbox
        id={'download-' + option}
        name={option}
        value={isTypeSelected(option)}
        onChange={(value) => {
          onToggle(value, option)
        }}
      />
      <Label htmlFor={'download-' + option} className='cursor-pointer'>
        {labels[option]}
      </Label>
    </div>
  )

  return (
    <div className='space-y-6'>
      <div className='flex flex-row gap-2'>
        <Label htmlFor='download-unit-select'>Units:</Label>
        <div>
          <Select
            name={'select-unit-system'}
            value={unit}
            onValueChange={onUnitChange}
          >
            <SelectTrigger id='download-unit-select' className='h-min w-full'>
              <SelectValue placeholder='' />
            </SelectTrigger>
            <SelectContent>
              {unitSystemOptions
                .filter((option) => option.name != 'Default')
                .map((unitSystem) => (
                  <SelectItem key={unitSystem.value} value={unitSystem.value}>
                    {unitSystem.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <h4 className='flex-1'>Select file types to download:</h4>
      <div className='space-y-8'>
        {/* aria-label duplicates the legend text: CheckboxGroup renders its
        legend inside a wrapping div rather than as the fieldset's direct
        child, which keeps browsers/AT from deriving the fieldset's
        accessible name from it natively. */}
        <CheckboxGroup
          legend='Uses selected units'
          aria-label='Uses selected units'
        >
          {unitScopedOptions.map(renderCheckbox)}
        </CheckboxGroup>
        <CheckboxGroup
          legend='Always original recorded units'
          aria-label='Always original recorded units'
        >
          {fixedUnitOptions.map(renderCheckbox)}
        </CheckboxGroup>
      </div>
    </div>
  )
}

export const DownloadForm = ({
  handleProceed,
  handleClose,
  sport,
  options,
  labels,
  cancelVariant
}: DownloadFormState) => {
  // Change from single type to array of selected types for multiselect
  const [selectedTypes, setSelectedTypes] = useState<SessionDownloadOption[]>(
    []
  )
  const unitSystem = useUnitsSystem(sport)
  const [unit, setUnit] = useState(unitSystem.name)

  // Modified to handle multiselect
  const handleCheckboxChange = (
    checked: boolean,
    type: SessionDownloadOption
  ) => {
    if (checked) {
      // Add the type if checked
      setSelectedTypes((prev) => [...prev, type])
    } else {
      // Remove the type if unchecked
      setSelectedTypes((prev) => prev.filter((t) => t !== type))
    }
  }

  return (
    <div className='space-y-6'>
      <DownloadOptionsList
        options={options}
        labels={labels}
        selected={selectedTypes}
        onToggle={handleCheckboxChange}
        unit={unit}
        onUnitChange={setUnit}
      />
      <div className={'flex justify-between'}>
        <Button
          onClick={() => {
            handleProceed(selectedTypes, unit.toLowerCase())
          }}
          disabled={selectedTypes.length === 0}
        >
          Proceed
        </Button>
        <Button variant={cancelVariant} onClick={handleClose}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
